/**
 * Toast 通知系統 | Toast Notification System
 * 提供即時的操作反饋與狀態通知
 * 注重使用者體驗：清楚、友善、不干擾
 */

import { useEffect, useState } from 'react';

export type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

interface ToastConfig {
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
}

// Toast 上下文管理器（簡單實現）
let toastListeners: ((toast: Toast) => void)[] = [];
let toastIdCounter = 0;

export const toast = {
  show: (config: ToastConfig) => {
    const id = `toast-${++toastIdCounter}`;
    const newToast: Toast = {
      id,
      type: config.type,
      title: config.title,
      message: config.message,
      duration: config.duration ?? (config.action ? 5000 : 3000),
      action: config.action,
    };

    toastListeners.forEach((listener) => listener(newToast));
    return id;
  },
  success: (title: string, message?: string, duration?: number) => {
    return toast.show({ type: 'success', title, message, duration });
  },
  error: (title: string, message?: string, duration?: number) => {
    return toast.show({ type: 'error', title, message, duration: duration ?? 5000 });
  },
  warning: (title: string, message?: string, duration?: number) => {
    return toast.show({ type: 'warning', title, message, duration });
  },
  info: (title: string, message?: string, duration?: number) => {
    return toast.show({ type: 'info', title, message, duration });
  },
  subscribe: (listener: (toast: Toast) => void) => {
    toastListeners.push(listener);
    return () => {
      toastListeners = toastListeners.filter((l) => l !== listener);
    };
  },
};

// Toast 組件
export function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsExiting(true);
      setTimeout(() => onRemove(toast.id), 300);
    }, toast.duration);

    return () => clearTimeout(timer);
  }, [toast.id, toast.duration, onRemove]);

  const getConfig = () => {
    switch (toast.type) {
      case 'success':
        return {
          icon: '✓',
          bg: 'bg-gradient-to-r from-green-50 to-emerald-50',
          border: 'border-green-300',
          iconBg: 'bg-green-500',
          titleColor: 'text-green-800',
        };
      case 'error':
        return {
          icon: '✕',
          bg: 'bg-gradient-to-r from-red-50 to-rose-50',
          border: 'border-red-300',
          iconBg: 'bg-red-500',
          titleColor: 'text-red-800',
        };
      case 'warning':
        return {
          icon: '⚠',
          bg: 'bg-gradient-to-r from-amber-50 to-orange-50',
          border: 'border-amber-300',
          iconBg: 'bg-amber-500',
          titleColor: 'text-amber-800',
        };
      case 'info':
        return {
          icon: 'ℹ',
          bg: 'bg-gradient-to-r from-blue-50 to-indigo-50',
          border: 'border-blue-300',
          iconBg: 'bg-blue-500',
          titleColor: 'text-blue-800',
        };
    }
  };

  const config = getConfig();

  return (
    <div
      className={`${config.bg} ${config.border} border-2 rounded-xl p-4 shadow-lg transform transition-all duration-300 ${
        isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'
      }`}
    >
      <div className="flex items-start gap-3">
        {/* 圖示 */}
        <div className={`${config.iconBg} w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 shadow-md`}>
          <span className="text-white text-sm font-bold">{config.icon}</span>
        </div>

        {/* 內容 */}
        <div className="flex-1 min-w-0">
          <h4 className={`font-bold ${config.titleColor} text-sm mb-1`}>
            {toast.title}
          </h4>
          {toast.message && (
            <p className="text-slate-600 text-xs leading-relaxed">
              {toast.message}
            </p>
          )}
          {/* 動作按鈕 */}
          {toast.action && (
            <button
              onClick={() => {
                toast.action!.onClick();
                setIsExiting(true);
                setTimeout(() => onRemove(toast.id), 300);
              }}
              className={`mt-2 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all active:scale-95 ${
                toast.type === 'success'
                  ? 'bg-green-500 text-white hover:bg-green-600 shadow-md shadow-green-500/20'
                  : toast.type === 'error'
                  ? 'bg-red-500 text-white hover:bg-red-600 shadow-md shadow-red-500/20'
                  : toast.type === 'warning'
                  ? 'bg-amber-500 text-white hover:bg-amber-600 shadow-md shadow-amber-500/20'
                  : 'bg-blue-500 text-white hover:bg-blue-600 shadow-md shadow-blue-500/20'
              }`}
            >
              {toast.action.label}
            </button>
          )}
        </div>

        {/* 關閉按鈕 */}
        <button
          onClick={() => {
            setIsExiting(true);
            setTimeout(() => onRemove(toast.id), 300);
          }}
          className="flex-shrink-0 w-6 h-6 rounded-md hover:bg-black/5 flex items-center justify-center transition-colors"
          aria-label="關閉"
        >
          <span className="text-slate-400 text-xs hover:text-slate-600">✕</span>
        </button>
      </div>

      {/* 進度條 */}
      <div className="mt-3 h-1 bg-black/10 rounded-full overflow-hidden">
        <div
          className="h-full bg-current opacity-30 rounded-full transition-all duration-100 ease-linear"
          style={{
            width: '100%',
            animation: `toast-progress ${toast.duration}ms linear forwards`,
          }}
        />
      </div>
    </div>
  );
}

// Toast 容器
export function ToastContainer() {
  const [toasts, setToasts] = useState<Toast[]>([]);

  useEffect(() => {
    const unsubscribe = toast.subscribe((newToast) => {
      setToasts((prev) => [...prev, newToast]);
    });

    return unsubscribe;
  }, []);

  const handleRemove = (id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-20 right-4 z-50 flex flex-col gap-3 max-w-sm w-full pointer-events-none">
      {toasts.map((toast) => (
        <div key={toast.id} className="pointer-events-auto">
          <ToastItem toast={toast} onRemove={handleRemove} />
        </div>
      ))}
    </div>
  );
}

// 添加進度條動畫到全局 CSS
export function ToastStyles() {
  return (
    <style>{`
      @keyframes toast-progress {
        from {
          width: 100%;
        }
        to {
          width: 0%;
        }
      }
    `}</style>
  );
}
